import React from 'react';

import styled, { css } from 'styled-components';

import InnerHtml from 'components/InnerHtml/InnerHtml';

import { DisclaimerBodyInterface } from 'interfaces/content-stack/modular-blocks/DisclaimerTypes';

interface SmallPrintProps {
  $color: string;
  $fontWeight: string;
  $fontSize?: string | undefined;
  $margin: {
    top?: string | undefined;
    bottom?: string | undefined;
    left?: string | undefined;
    right?: string | undefined;
  };
  $hasImage: boolean;
}

export const SmallPrint = styled.div<SmallPrintProps>(
  ({ $color, $fontWeight, $fontSize, $margin, $hasImage }) => css`
    width: 100%;
    max-width: 1280px;
    margin-left: auto;
    margin-right: auto;
    padding-left: 5rem;
    padding-right: 5rem;
    padding-top: 0;
    padding-bottom: 1rem;
    position: relative;
    font-weight: 500;
    font-size: ${$fontSize ?? `0.875rem`};
    font-family: ${(props) => props.theme.fonts.default};

    @media screen and (min-width: 1440px) {
      padding-left: 0;
      padding-right: 0;
    }

    @media screen and (min-width: 1920px) {
      max-width: 1340px;
    }

    @media screen and (max-width: ${(props) => props.theme.breakpoints.lg}) {
      width: 100%;
      padding-left: 3rem;
      padding-right: 3rem;
      padding-bottom: 0.25rem;
    }

    @media screen and (max-width: ${(props) => props.theme.breakpoints.md}) {
      padding-left: 1.5rem;
      padding-right: 1.5rem;
    }

    @media screen and (max-width: ${(props) => props.theme.breakpoints.md}) {
      padding-left: 1rem;
      padding-right: 1rem;
    }

    &&& {
      a {
        color: ${(props) => props.theme.colors.black};
        transition: color 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
        text-decoration: underline;
        font-weight: 600;

        &:hover {
          color: ${(props) => props.theme.colors.primaryRed};
        }
      }

      sup {
        top: -0.5em;
        vertical-align: baseline;
        font-size: 75%;
        line-height: 0;
        position: relative;
      }
    }

    ${
      $hasImage &&
      css`
        p {
          display: inline;
          margin-left: -0.5rem;
          margin-right: -0.3rem;

          img {
            display: inline;
            height: 1rem;
          }
        }
      `
    }

    ${
      $margin.top &&
      css`
        margin-top: ${$margin.top}rem;
      `
    }

  ${
    $margin.bottom &&
    css`
      margin-bottom: ${$margin.bottom}rem;
    `
  }

  ${
    $fontWeight &&
    css`
      font-weight: ${$fontWeight} !important;
    `
  }

  ${
    $color === 'black'
      ? css`
          color: ${(props) => props.theme.colors.black};
        `
      : $color === 'red'
        ? css`
            color: ${(props) => props.theme.colors.primaryRed};
          `
        : $color === 'grey'
          ? css`
              color: ${(props) => props.theme.colors.gray};
            `
          : $color === 'light_grey'
            ? css`
                color: ${(props) => props.theme.colors.lightGray};
              `
            : $color === 'dark_grey' &&
              css`
                color: ${(props) => props.theme.colors.darkGray};
              `
  })}
  `
);

interface DisclaimerBodyTypes {
  body: DisclaimerBodyInterface;
  fontSize?: string | undefined;
}

const DisclaimerBody: React.FC<DisclaimerBodyTypes> = ({ body, fontSize }) => {
  if (!body) {
    return null;
  }

  const { fontWeight, textStyling, content, margin } = body;

  return (
    <SmallPrint
      $color={textStyling}
      $fontWeight={fontWeight}
      $fontSize={fontSize}
      $margin={margin}
      $hasImage={content.indexOf('<img') !== -1}
    >
      <InnerHtml richText={content} />
    </SmallPrint>
  );
};

export default React.memo(DisclaimerBody);
